import { ReactNode, useMemo, useRef, useState } from 'react';
import * as Yup from 'yup';

import { FormProvider } from 'components/forms/form/Form';
import { AsyncTypeAheadFieldInstance } from 'components/forms/typeAheadField/AsyncTypeAheadField.types';
import useAppUser from 'hooks/useAppUser';
import useLocalStorage from 'hooks/useLocalStorage';
import FilterFormContext from './FilterFormContext';

interface FilterFormContextProviderProps<
  T,
  S extends Yup.AnySchema<T> = Yup.AnySchema<T>,
> {
  children: ReactNode;
  defaultValues: T;
  localStorageKeyPrefix: string;
  schema: S;
}

const FilterFormContextProvider = <T,>({
  children,
  defaultValues,
  localStorageKeyPrefix,
  schema,
}: FilterFormContextProviderProps<T>) => {
  const { userUid } = useAppUser();
  const searchFieldRef = useRef<AsyncTypeAheadFieldInstance>();
  const filtersLocalStorageKey = `${localStorageKeyPrefix}${userUid as string}`;

  const { getValue: getStoredFormValues } = useLocalStorage<T>(
    filtersLocalStorageKey,
  );
  const storedFormValues = getStoredFormValues();
  const [lastSubmittedFilters, setLastSubmittedFilters] = useState<T | null>(
    storedFormValues ? schema.cast(storedFormValues) : null,
  );

  const contextValue = useMemo(
    () => ({
      lastSubmittedFilters,
      searchFieldRef,
      filtersLocalStorageKey,
      schema,
      setLastSubmittedFilters,
    }),
    [
      lastSubmittedFilters,
      searchFieldRef,
      filtersLocalStorageKey,
      schema,
      setLastSubmittedFilters,
    ],
  );

  return (
    <FilterFormContext.Provider value={contextValue}>
      <FormProvider defaultValues={defaultValues} resolder={schema}>
        {children}
      </FormProvider>
    </FilterFormContext.Provider>
  );
};

export default FilterFormContextProvider;
